export interface HubspotShareForm {
  region: string
  portalId: string
  formId: string
  embedScriptUrl: string
}

// share.hsforms.com (na1) or share-<region>.hsforms.com
const SHARE_HOST_PATTERN = /^share(?:-([a-z0-9]+))?\.hsforms\.com$/
// '2' (new forms editor) + base64url form GUID (22 chars) + base36 portal id.
// Legacy '1' links are not supported by the v4 embed, so they stay plain links.
const SHARE_SLUG_PATTERN = /^2([A-Za-z0-9_-]{22})([0-9a-z]+)$/

const base64UrlToUuid = (value: string): string | null => {
  const bytes = atob(value.replace(/-/g, '+').replace(/_/g, '/') + '==')
  const hex = Array.from(bytes, (char) =>
    char.charCodeAt(0).toString(16).padStart(2, '0')
  ).join('')
  if (hex.length !== 32) return null

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-')
}

/**
 * Decodes a HubSpot form share link (e.g. CMS CTA urls like
 * https://share-eu1.hsforms.com/2N0ahngDkTB-W40WW2srZpgewif8) into the ids
 * needed to embed the same form inline. Returns null for anything else.
 */
export const parseHubspotShareUrl = (
  url: string | null | undefined
): HubspotShareForm | null => {
  if (!url) return null

  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }

  const hostMatch = parsed.hostname.match(SHARE_HOST_PATTERN)
  const slugMatch = parsed.pathname
    .replace(/^\/+|\/+$/g, '')
    .match(SHARE_SLUG_PATTERN)
  if (!hostMatch || !slugMatch) return null

  const formId = base64UrlToUuid(slugMatch[1]!)
  const portalId = parseInt(slugMatch[2]!, 36)
  if (!formId || !Number.isSafeInteger(portalId)) return null

  const region = hostMatch[1] ?? 'na1'
  const scriptHost =
    region === 'na1' ? 'js.hsforms.net' : `js-${region}.hsforms.net`

  return {
    region,
    portalId: String(portalId),
    formId,
    embedScriptUrl: `https://${scriptHost}/forms/embed/${portalId}.js`,
  }
}
